import React, { FC, useState, useEffect, useMemo } from "react";
import { Modal, Button, Typography } from "antd";
import { ExternalMatter } from "../../data-types";
import { useFilteredMattersLazyQuery } from "~/hooks/matters";
import { MatterStatus } from "@design-system";
import { InfiniteScrollSelect } from "../infinite-scroll-select";
import { DefaultOptionType } from "antd/lib/select";

export type LinkExternalMatterModalProps = {
  open: boolean;
  onClose: () => void;
  externalMatter: ExternalMatter | null;
  onLinkExternalMatterToMatter: (externalMatterId: number, matterId: number) => Promise<void>;
  currentUserId: number;
  pmsName: string;
};

export const LinkExternalMatterModal: FC<LinkExternalMatterModalProps> = ({
  open,
  onClose,
  externalMatter,
  onLinkExternalMatterToMatter,
  currentUserId,
  pmsName,
}) => {
  const [textFilter, setTextFilter] = useState<string>("");
  const [selectedMatterId, setSelectedMatterId] = useState<number | undefined>(undefined);
  const [currencyCodeMismatch, setCurrencyCodeMismatch] = useState(false);

  const { callFunc, data, loading, fetchMore, total } = useFilteredMattersLazyQuery({
    filter: {
      ...(textFilter && { title: textFilter }),
      ...(externalMatter?.client && { clientId: [externalMatter.client.id] }),
      status: [MatterStatus.Draft],
      createdBy: [currentUserId],
      template: false,
      linked: false,
    },
    orderBy: [{ updatedAt: "desc" }],
    offset: 0,
    limit: 10,
  });

  useEffect(() => {
    if (open) {
      setSelectedMatterId(undefined);
      setTextFilter("");
      callFunc();
    }
  }, [open]);

  useEffect(() => {
    if (textFilter) {
      callFunc();
    }
  }, [textFilter]);

  const doOnLoadMore =
    (data?.filteredMatters?.matters?.length || 0) >= (total ?? 0)
      ? undefined
      : () => {
          fetchMore();
        };

  const matterOptions = useMemo<DefaultOptionType[]>(
    () =>
      data?.filteredMatters?.matters?.map((matter) => ({
        ...matter,
        value: matter.id,
        label: matter.title,
      })) || [],
    [data?.filteredMatters?.matters]
  );

  useEffect(() => {
    const selectedMatter = matterOptions.find((option) => option.value === selectedMatterId);
    setCurrencyCodeMismatch(
      externalMatter?.currency?.code &&
        selectedMatter?.currency?.code &&
        externalMatter.currency.code !== selectedMatter.currency.code
    );
  }, [selectedMatterId]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={"Link to VPD matter"}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => {
            if (selectedMatterId && externalMatter) {
              onLinkExternalMatterToMatter(externalMatter.id, selectedMatterId);
            }
            onClose();
          }}
          disabled={!selectedMatterId || currencyCodeMismatch}
        >
          OK
        </Button>,
      ]}
    >
      <Typography.Text>
        Select a VPD matter to link to the {pmsName} matter:{" "}
        {externalMatter?.title ? <strong>{externalMatter.title}</strong> : ""}.
      </Typography.Text>
      <InfiniteScrollSelect
        style={{ marginTop: "10px" }}
        showSearch={true}
        allowClear={true}
        onClear={() => {
          setSelectedMatterId(undefined);
          setTextFilter("");
        }}
        onLoadMore={doOnLoadMore}
        loading={loading}
        options={matterOptions}
        dropdownRender={(menu) => <div>{menu}</div>}
        onChange={(value) => {
          setSelectedMatterId(value);
        }}
        value={matterOptions.find((option) => option.value === selectedMatterId)?.label}
        onSearch={(value) => {
          setTextFilter(value);
        }}
        filterOption={false}
      />
      {currencyCodeMismatch && (
        <div style={{ color: "red" }}>
          The currency of the selected matter does not match the currency of the {pmsName} matter.
        </div>
      )}
    </Modal>
  );
};
